import { useCurrentProject } from "../lib/projects/project-context";
import { projectDisplayName } from "../lib/projects/schema";
import { Icon } from "./ui/Icon";

// Repère de contexte affiché en haut de chaque écran : montre le projet courant
// et, dès qu'il y a ≥2 projets, permet d'en changer SUR PLACE (sans navigation).
// Le changement est global (toute l'app lit le même ProjectContext).
//
// Choix du <select> : accessible nativement et, sur iOS (Capacitor), il ouvre le
// picker natif au doigt — meilleure UX qu'un popover maison. 1 seul projet → label
// statique (rien à changer).

interface ProjectContextBadgeProps {
  className?: string;
}

export function ProjectContextBadge({ className = "" }: ProjectContextBadgeProps) {
  const {
    projects,
    currentProjectId,
    currentProject,
    setCurrentProject,
    loading,
    erreur,
    rechargerProjets,
  } = useCurrentProject();

  if (loading) return null;

  const wrapperClass = ["proj-context", className].filter(Boolean).join(" ");

  // Le chargement a raté : on le DIT. Disparaître en silence, c'est ce qui a
  // laissé enregistrer 40 minutes dans le vide le 17 août.
  if (erreur) {
    return (
      <p className={[wrapperClass, "proj-context--erreur"].join(" ")}>
        <Icon name="folder" size={15} className="flex-none" />
        <span>Projets non chargés</span>
        <button type="button" className="proj-context-retry" onClick={() => rechargerProjets()}>
          <Icon name="rotate-cw" size={13} />
          Réessayer
        </button>
      </p>
    );
  }

  if (!currentProject || !currentProjectId) return null;

  // Un seul projet : pas d'affordance de changement.
  if (projects.length < 2) {
    return (
      <p className={wrapperClass}>
        <Icon name="folder" size={15} className="flex-none" />
        <span className="proj-context-name">{projectDisplayName(currentProject)}</span>
      </p>
    );
  }

  return (
    <label className={wrapperClass}>
      <Icon name="folder" size={15} className="flex-none" />
      <span className="sr-only">Projet courant</span>
      <select
        className="proj-context-select"
        value={currentProjectId}
        onChange={(e) => setCurrentProject(e.target.value)}
        aria-label="Projet courant"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {projectDisplayName(p)}
          </option>
        ))}
      </select>
    </label>
  );
}
